import React, { useState, useEffect, useCallback } from "react";
import PropTypes from "prop-types";
import qs from "query-string";
import InfinteScroll from "./InfiniteScroll";
import YaMetrika from "./YaMetrika";
import CoreContext from "../utils/context";
import {loadHeadScripts} from '../services/loadHeadScripts'
import { getUserId} from "../utils/userId";
import { generateId } from "../utils/showId";
import { shouldShowMainLogo } from "../utils/domain";
import WebPushScript from "../services/WebPushScript";

const Core = (props) => {
    const [context, setContext] = useState(() => {
        const initialContext = {
            screen: props.screenProvider.detectScreen(),
            sid: null,
            rcid: null,
            fpr: null,
            fprVersion: null,
            queryString: qs.parse(window.location.search)
        };

        Object.assign(initialContext, props.context || {});

        window.getSmiParams = (blockId) => {
            const params = {
                sid: initialContext.sid,
                subid_subid2: initialContext.analytics_lable
            };
            if (initialContext.utm_term) {
                params.subid_source = initialContext.utm_term;
            }
            return params;
        };

        return initialContext;
    });

    const [loading, setLoading] = useState(false);
    const [data, setData] = useState({});
    const [scrollLoading, setScrollLoading] = useState(false);
    const [scrollData, setScrollData] = useState([]);
    const [lastLoadTeasers, setLastLoadTeasers] = useState([]);
    const [scrollNum, setScrollNum] = useState(0);
    const [currency, setCurrency] = useState({
        usd: 0,
        eur: 0
    });
    const [userId, setUserId] = useState(null);
    const [showId, setShowId] = useState(null);
    
    const url = new URL(window.location.href);
    const fprParam = url.searchParams.get('fpr') || context.fpr;
    const fprVersion = fprParam.split('_').slice(1).join('_') || context.fprVersion;

    const isFull = context.isFull;
    const isDesktop = context.screen >= 960;
    const showPopUp = context.pop_up !== undefined ? context.pop_up : true
    const showBackClick = context.back_click !== undefined ? context.back_click : true
    const showSubscriptions = context.collect_subscriptions !== undefined ? context.collect_subscriptions : true
    const subscriptionType = context.subscription_format !== undefined ? context.subscription_format : ''
    const refreshTime = context.refresh_time || null
    const popUpType = context.popup_type || "replace"
    const showMainLogo = context.show_main_logo;
    const currentDomain = window.location.hostname;
    const domain = context.domain;
    
    // Определяем показ лого на основе реального домена
    const shouldShowMainLogoValue = shouldShowMainLogo(currentDomain);

    const onBlockMount = useCallback((blockProps) => {
        props.eventHandler.onBlockMount(context, blockProps);
    }, [context, props.eventHandler]);

    const onTeaserClick = useCallback((teaserProps = {}) => {
        props.eventHandler.onTeaserClick(context, teaserProps);
    }, [context, props.eventHandler]);

    const onNewsClick = useCallback(() => {
        const newProps = {};

        if (props.nextNews) {
            newProps.redirect = props.nextNews.preview_url;
        }

        props.eventHandler.onNewsClick(context, newProps);
    }, [context, props.eventHandler, props.nextNews]);

    const onInternalClick = useCallback((internalProps = {}) => {
        props.eventHandler.onInternalClick(context, internalProps);
    }, [context, props.eventHandler]);

    const onRecommendationClick = useCallback((recommendationProps = {}) => {
        props.eventHandler.onRecommendationClick(context, recommendationProps);
    }, [context, props.eventHandler]);

    const onCategoryClick = useCallback((categoryProps) => {
        props.eventHandler.onCategoryClick(context, categoryProps);
    }, [context, props.eventHandler]);

    const onFirstLoad = useCallback(() => {
        setLoading(true);
        setData({});
        setScrollData([]);
        setScrollNum(0);

        props.dataProvider.loadInitialData(context).then(initialData => {
            const { data: initialDataData, scrollData: initialScrollData = [], lastLoadTeasers: initialLastLoadTeasers = [] } = initialData;
            setLoading(false);
            setData(initialDataData);
            setScrollData(initialScrollData);
            setLastLoadTeasers(initialLastLoadTeasers);
        });
    }, [context, props.dataProvider]);

    const onScrollLoad = useCallback(() => {
        if (scrollLoading) {
            return;
        }

        if (props.scrollLimit && scrollNum >= props.scrollLimit) {
            return;
        }

        setScrollLoading(true);

        props.eventHandler.onScrollLoad(context, scrollNum);

        props.dataProvider.loadScrollData(context, scrollNum).then(result => {
            let { scrollData: newScrollData, lastLoadTeasers: newLastLoadTeasers = [] } = result;
            newScrollData = scrollData.concat(newScrollData);

            setScrollLoading(false);
            setScrollNum(prevScrollNum => prevScrollNum + 1);
            setScrollData(newScrollData);
            setLastLoadTeasers(newLastLoadTeasers);
        });
    }, [scrollLoading, props.scrollLimit, scrollNum, context, props.eventHandler, props.dataProvider, scrollData]);

    const handleResize = useCallback(() => {
        const screen = props.screenProvider.detectScreen();
        if (context.screen !== screen) {
            setContext(prevContext => ({ ...prevContext, screen }));
        }
    }, [context.screen, props.screenProvider]);

    useEffect(() => {
        window.addEventListener("resize", handleResize);
        props.eventHandler.onCoreMount(context);
        onFirstLoad();

        return () => {
            window.removeEventListener("resize", handleResize);
        };
    }, []);

    useEffect(() => {
        onFirstLoad();
    }, [context.screen]);

    useEffect(() => {
        loadHeadScripts([
            'https://yandex.ru/ads/system/header-bidding.js',
            'https://yandex.ru/ads/system/context.js',
        ]);
    }, []);

    useEffect(() => {
        const initializeIds = async () => {
            const id = getUserId();
            setUserId(id);

            const newShowId = generateId();
            setShowId(newShowId);
        };

        initializeIds();
    }, []);

    return (
        <InfinteScroll onNextLoad={onScrollLoad}>
            <YaMetrika id={45661026} />
            {showSubscriptions && <WebPushScript activeWidget={subscriptionType}/>}
            <CoreContext.Provider value={context}>
                {props.children({
                    onBlockMount,
                    onNewsClick,
                    onTeaserClick,
                    onCategoryClick,
                    onInternalClick,
                    onRecommendationClick,

                    loading,
                    scrollLoading,

                    data,
                    scrollData,
                    lastLoadTeasers,

                    news: props.news,
                    nextNews: props.nextNews,
                    categories: props.categories,

                    currency,
                    geoCity: props.geoCity,
                    use_html_render: props.use_html_render,

                    context,
                    isFull,
                    isDesktop,
                    showPopUp,
                    showBackClick,
                    showSubscriptions,
                    subscriptionType,
                    refreshTime,
                    popUpType,
                    showMainLogo: shouldShowMainLogoValue,
                    fprVersion,
                    currentDomain,
                    userId,
                    showId
                })}
            </CoreContext.Provider>
        </InfinteScroll>
    );
};

Core.propTypes = {
    dataProvider: PropTypes.object.isRequired,
    screenProvider: PropTypes.object.isRequired,
    eventHandler: PropTypes.object.isRequired,
    scrollLimit: PropTypes.number,
    children: PropTypes.func.isRequired
};

Core.defaultProps = {
    geoCity: "",
    scrollLimit: 0
};

export default Core;
